import React from 'react'
import { Icon } from 'antd'

import {
  getPhaseStatus,
  getPhaseView,
  getStatusLabel,
  isPhaseSelectable
} from '../../utils'
import './ProgressPreview.scss'

const normalizeStatus = status => {
  const value = String(status || '').toLowerCase()

  if (value === 'completed' || value === 'complete') return 'complete'
  if (value === 'current' || value === 'in_progress') return 'active'
  return 'pending'
}

const ProgressPreview = ({
  phaseSteps,
  phaseProgress,
  currentPhase,
  expandedPhases,
  progressPercent,
  isFinalized,
  onPhaseSelect = null,
  isCollapsed = false,
  onToggleCollapse = null
}) => {
  const safePercent = Math.max(
    0,
    Math.min(100, Math.round(progressPercent || 0))
  )
  const apiPhaseByKey = (phaseProgress || []).reduce((result, phase) => {
    const key = phase?.phase || phase?.key
    if (key) result[key] = phase
    return result
  }, {})
  const workflowSteps = phaseProgress?.length
    ? phaseProgress.map(phase => ({
        ...phase,
        key: phase.phase || phase.key
      }))
    : phaseSteps

  return (
    <nav
      className={`creative-direction-chat__process${
        isCollapsed ? ' creative-direction-chat__process--collapsed' : ''
      }`}
      aria-label="Creative direction progress"
    >
      <div className="creative-direction-chat__process-shell">
        <div className="creative-direction-chat__process-topbar">
          <button
            type="button"
            className="creative-direction-chat__process-menu"
            aria-label="Collapse creative direction progress"
            onClick={onToggleCollapse}
          >
            <Icon type="menu" />
          </button>
          <button
            type="button"
            className="creative-direction-chat__process-back"
            aria-label="Collapse creative direction progress"
            onClick={onToggleCollapse}
          >
            <Icon type="arrow-left" />
          </button>
        </div>

        <div className="creative-direction-chat__process-progress">
          <div className="creative-direction-chat__process-progress-row">
            <span>Overall progress</span>
            <strong>{safePercent}%</strong>
          </div>
          <div className="creative-direction-chat__process-progress-track">
            <span style={{ width: `${safePercent}%` }} />
          </div>
        </div>

        <div className="creative-direction-chat__phase-list">
          {workflowSteps.map(step => {
            const apiPhase = apiPhaseByKey[step.key]
            const view = getPhaseView(step.key)
            const status = apiPhase
              ? normalizeStatus(apiPhase.status)
              : getPhaseStatus({
                  phaseSteps,
                  currentPhase,
                  phase: step.key,
                  isFinalized
                })
            const canSelect =
              Boolean(onPhaseSelect) &&
              isPhaseSelectable({
                phaseSteps,
                currentPhase,
                phase: step.key,
                isFinalized
              })
            const isSelected = Boolean(expandedPhases[step.key])
            const subphases = apiPhase?.subphases?.length
              ? apiPhase.subphases
              : Array.from({
                  length: step.key === 'finalized' ? 1 : 3
                }).map((_, index) => ({
                  key: `${step.key}-${index}`,
                  label: `Subpage ${index + 1}`,
                  status:
                    status === 'complete'
                      ? 'completed'
                      : status === 'active' && index === 0
                      ? 'current'
                      : 'pending'
                }))

            return (
              <div
                className={`creative-direction-chat__phase-group creative-direction-chat__phase-group--${status}${
                  isSelected
                    ? ' creative-direction-chat__phase-group--selected'
                    : ''
                }`}
                key={step.key}
              >
                <button
                  type="button"
                  className="creative-direction-chat__phase-group-heading"
                  disabled={!canSelect}
                  onClick={() => canSelect && onPhaseSelect(step.key)}
                >
                  <strong>{apiPhase?.label || view.shortLabel}</strong>
                  <small>
                    {apiPhase?.percent !== undefined
                      ? `${apiPhase.percent}% · `
                      : ''}
                    {getStatusLabel(status)}
                  </small>
                </button>
                {subphases.map((subphase, subpageIndex) => {
                  const normalizedSubphaseStatus = normalizeStatus(
                    subphase.status
                  )
                  const subpageStatus =
                    normalizedSubphaseStatus === 'pending' &&
                    isSelected &&
                    subpageIndex === 0
                      ? 'selected'
                      : normalizedSubphaseStatus

                  return (
                    <button
                      type="button"
                      className={`creative-direction-chat__phase-item creative-direction-chat__phase-item--${subpageStatus}`}
                      disabled={!canSelect}
                      key={subphase.key || `${step.key}-${subpageIndex}`}
                      onClick={() => canSelect && onPhaseSelect(step.key)}
                    >
                      <span className="creative-direction-chat__phase-item-node">
                        {subpageStatus === 'complete' ? (
                          <Icon type="check" />
                        ) : (
                          subpageIndex + 1
                        )}
                      </span>
                      <span className="creative-direction-chat__phase-item-copy">
                        <strong>{subphase.label}</strong>
                        <small>{getStatusLabel(subpageStatus)}</small>
                      </span>
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </nav>
  )
}

export default ProgressPreview
